import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Divider,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Chip,
  Stack
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Person,
  Lock,
  CheckCircle,
  Cancel,
  Security,
  Timer
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';

// Helper functie om runtime configuratie te gebruiken
const getCentralBackendURL = () => {
  // Runtime configuratie (Docker container)
  if (window.ENV_CONFIG && window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL) {
    return window.ENV_CONFIG.REACT_APP_CENTRAL_BACKEND_URL;
  }
  // Build-time configuratie (fallback)
  return process.env.REACT_APP_CENTRAL_BACKEND_URL || 'http://localhost:8001';
};

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.9),
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.5),
      },
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
  marginBottom: theme.spacing(2.5)
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    transform: 'none',
    boxShadow: 'none',
  }
}));

const ModeToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 500,
  padding: theme.spacing(1, 2),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
    borderColor: theme.palette.primary.main,
  }
}));

const PasswordStrengthBar = styled(LinearProgress)(({ theme }) => ({
  height: 4,
  borderRadius: 2,
  marginTop: theme.spacing(1),
  '& .MuiLinearProgress-bar': {
    borderRadius: 2,
  }
}));

// Add pulse keyframe animation
const pulseKeyframes = `
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
`;

const LocalAuth = ({ onSwitchToOAuth }) => {
  const { login } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Rate limiting state
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const intervalRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: ''
  });

  // Add pulse animation styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = pulseKeyframes;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (countdownSeconds > 0) {
      intervalRef.current = setInterval(() => {
        setCountdownSeconds(prev => {
          if (prev <= 1) {
            setRateLimitInfo(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [countdownSeconds]);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    // Clear errors when user types
    if (error) setError(null);
    // Clear rate limit info when user starts typing again
    if (rateLimitInfo) {
      setRateLimitInfo(null);
      setCountdownSeconds(0);
    }
  };

  // Password strength calculation
  const getPasswordStrength = (password) => {
    let score = 0;
    const requirements = [];

    if (password.length >= 8) {
      score += 20;
      requirements.push({ text: 'Min. 8 karakters', met: true });
    } else {
      requirements.push({ text: 'Min. 8 karakters', met: false });
    }

    if (/[a-z]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Kleine letter', met: true });
    } else {
      requirements.push({ text: 'Kleine letter', met: false });
    }

    if (/[A-Z]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Hoofdletter', met: true });
    } else {
      requirements.push({ text: 'Hoofdletter', met: false });
    }

    if (/[0-9]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Cijfer', met: true });
    } else {
      requirements.push({ text: 'Cijfer', met: false });
    }

    if (/[^A-Za-z0-9]/.test(password)) {
      score += 20;
      requirements.push({ text: 'Speciaal teken', met: true });
    } else {
      requirements.push({ text: 'Speciaal teken', met: false });
    }

    return { score, requirements };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  const getStrengthColor = (score) => {
    if (score < 40) return 'error';
    if (score < 80) return 'warning';
    return 'success';
  };

  const getStrengthText = (score) => {
    if (score < 40) return 'Zwak';
    if (score < 80) return 'Gemiddeld';
    return 'Sterk';
  };

  const formatTime = (seconds) => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Email en wachtwoord zijn verplicht');
      return false;
    }

    if (mode === 'register') {
      if (!formData.name) {
        setError('Naam is verplicht');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Wachtwoorden komen niet overeen');
        return false;
      }
      if (passwordStrength.score < 80) {
        setError('Wachtwoord voldoet niet aan alle vereisten');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const endpoint = mode === 'login' 
        ? `${getCentralBackendURL()}/api/v1/auth/local/login`
        : `${getCentralBackendURL()}/api/v1/auth/local/register`;

      const requestData = mode === 'login' 
        ? {
            email: formData.email,
            password: formData.password
          }
        : {
            email: formData.email,
            name: formData.name,
            password: formData.password,
            confirm_password: formData.confirmPassword
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (response.ok) {
        if (mode === 'login') {
          // Login successful
          const loginSuccess = await login(data.access_token);
          if (!loginSuccess) {
            setError('Login mislukt. Probeer opnieuw.');
          }
        } else {
          // Registration successful
          if (data.access_token) {
            setSuccess('Account succesvol aangemaakt! U wordt doorgestuurd...');
            // Auto login after registration if approved
            setTimeout(async () => {
              const loginSuccess = await login(data.access_token);
              if (!loginSuccess) {
                setSuccess('Account aangemaakt! U kunt nu inloggen.');
                setMode('login');
              }
            }, 1500);
          } else {
            setSuccess('Account aangemaakt! Wacht op goedkeuring van een beheerder voordat u kunt inloggen.');
            setMode('login');
          }
        }
      } else {
        // Handle errors
        if (response.status === 429 && data.detail) {
          // Rate limiting error
          if (typeof data.detail === 'object' && data.detail.type === 'rate_limit_exceeded') {
            setRateLimitInfo(data.detail);
            setCountdownSeconds(data.detail.retry_after_seconds || 0);
            setError(null); // Don't show as regular error
          } else {
            setError(data.detail.message || data.detail);
          }
        } else if (data.detail) {
          if (typeof data.detail === 'object' && data.detail.errors) {
            setError(data.detail.errors.join(', '));
          } else if (typeof data.detail === 'object' && data.detail.message) {
            setError(data.detail.message);
          } else {
            setError(data.detail);
          }
        } else {
          setError(mode === 'login' ? 'Login mislukt' : 'Registratie mislukt');
        }
      }
    } catch (err) {
      console.error(`${mode} error:`, err);
      setError('Kan geen verbinding maken met de server. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === 'login' ? 'register' : 'login');
    setError(null);
    setSuccess(null);
    setRateLimitInfo(null);
    setCountdownSeconds(0);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: ''
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ fontWeight: 600, mb: 1 }}>
        {mode === 'login' ? 'Welkom terug!' : 'Account aanmaken'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 1.5 }}>
          {success}
        </Alert>
      )}

      {rateLimitInfo && countdownSeconds > 0 && (
        <Alert 
          severity="warning" 
          sx={{ 
            mb: 2, 
            borderRadius: 1.5,
            background: 'linear-gradient(45deg, #fff3e0, #ffe0b2)',
            border: '1px solid #ffb74d'
          }}
          icon={<Timer />}
        >
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              Te veel login pogingen
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {rateLimitInfo.message}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Wacht nog:
              </Typography>
              <Chip 
                label={formatTime(countdownSeconds)}
                size="small"
                color="warning"
                sx={{ 
                  fontWeight: 600,
                  minWidth: '60px',
                  animation: 'pulse 1s ease-in-out infinite'
                }}
              />
            </Box>
            {rateLimitInfo.attempt_count > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Aantal pogingen: {rateLimitInfo.attempt_count}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ space: 2 }}>
        <StyledTextField
          fullWidth
          type="email"
          label="Email adres"
          value={formData.email}
          onChange={handleInputChange('email')}
          disabled={loading}
          required
          autoComplete="email"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email color="action" />
              </InputAdornment>
            ),
          }}
        />

        {mode === 'register' && (
          <StyledTextField
            fullWidth
            label="Volledige naam"
            value={formData.name}
            onChange={handleInputChange('name')}
            disabled={loading}
            required
            autoComplete="name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Person color="action" />
                </InputAdornment>
              ),
            }}
          />
        )}

        <StyledTextField
          fullWidth
          type={showPassword ? 'text' : 'password'}
          label="Wachtwoord"
          value={formData.password}
          onChange={handleInputChange('password')}
          disabled={loading}
          required
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {mode === 'register' && formData.password && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Security fontSize="small" color="action" sx={{ mr: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Wachtwoordsterkte: {getStrengthText(passwordStrength.score)}
              </Typography>
            </Box>
            <PasswordStrengthBar 
              variant="determinate" 
              value={passwordStrength.score} 
              color={getStrengthColor(passwordStrength.score)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              {passwordStrength.requirements.map((req, index) => (
                <Chip
                  key={index}
                  size="small"
                  icon={req.met ? <CheckCircle /> : <Cancel />}
                  label={req.text}
                  color={req.met ? 'success' : 'default'}
                  variant={req.met ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.75rem' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {mode === 'register' && (
          <StyledTextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Bevestig wachtwoord"
            value={formData.confirmPassword}
            onChange={handleInputChange('confirmPassword')}
            disabled={loading}
            required
            autoComplete="new-password"
            error={formData.confirmPassword && formData.password !== formData.confirmPassword}
            helperText={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'Wachtwoorden komen niet overeen' : ''}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        <StyledButton
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || (mode === 'login' && countdownSeconds > 0)}
          sx={{ 
            mb: 2,
            ...(countdownSeconds > 0 && mode === 'login' && {
              background: '#bdbdbd',
              color: '#ffffff',
              '&:hover': {
                background: '#bdbdbd',
                transform: 'none',
                boxShadow: 'none'
              }
            })
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: 'white' }} />
          ) : countdownSeconds > 0 && mode === 'login' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Timer />
              Wacht {formatTime(countdownSeconds)}
            </Box>
          ) : (
            mode === 'login' ? 'Inloggen' : 'Account Aanmaken'
          )}
        </StyledButton>

        {/* Wachtwoord vergeten link alleen bij login */}
        {mode === 'login' && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <RouterLink
              to="/forgot-password"
              style={{
                color: '#666',
                textDecoration: 'none',
                fontSize: '0.875rem'
              }}
            >
              Wachtwoord vergeten?
            </RouterLink>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {mode === 'login' ? 'Nog geen account?' : 'Al een account?'}
        </Typography>
        <ModeToggleButton
          onClick={switchMode}
          disabled={loading}
        >
          {mode === 'login' ? 'Account aanmaken' : 'Inloggen'}
        </ModeToggleButton>
      </Box>

      {/* OAuth link is tijdelijk uitgeschakeld - OAuth functionaliteit wordt later weer geactiveerd */}
      {/*
      {onSwitchToOAuth && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={onSwitchToOAuth}
            sx={{ 
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: 'primary.main'
              }
            }}
          >
            Of log in met Google/Microsoft →
          </Link>
        </Box>
      )}
      */}
    </Box>
  );
};

export default LocalAuth; 